import {
    featureService,
    type OutcomeLedgerSettingsResponse,
} from '@common/api/featureService';
import { useCallback, useEffect, useState } from 'react';
import {
    readStoredOutcomeSummarySettings,
    writeStoredOutcomeSummarySettings,
} from './outcomeSummarySettingsStorage';

export type OutcomeSummarySettingsStatus = 'idle' | 'loading' | 'ready' | 'error';

export type OutcomeSummarySettings = {
    enabled: boolean;
    refreshHourLocal: number;
    refreshMinuteLocal: number;
    refreshLocalTime: string;
    timezone: string | null;
    lastGeneratedAt: string | null;
    privacyAcceptedAt: string | null;
    privacyNoticeVersion: string | null;
};

const DEFAULT_OUTCOME_SUMMARY_SETTINGS: OutcomeSummarySettings = {
    enabled: false,
    refreshHourLocal: 8,
    refreshMinuteLocal: 0,
    refreshLocalTime: '08:00',
    timezone: null,
    lastGeneratedAt: null,
    privacyAcceptedAt: null,
    privacyNoticeVersion: null,
};

const OUTCOME_SUMMARY_PRIVACY_NOTICE_VERSION = 'outcome-summary-privacy-v1';

const asBoolean = (value: unknown, fallback: boolean) => (
    typeof value === 'boolean' ? value : fallback
);

const asRefreshHour = (value: unknown, fallback: number) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric >= 0 && numeric <= 23
        ? numeric
        : fallback;
};

const asRefreshMinute = (value: unknown, fallback: number) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric >= 0 && numeric <= 59
        ? numeric
        : fallback;
};

const padTimePart = (value: number) => String(value).padStart(2, '0');

const normalizeRefreshLocalTime = (
    rawTime: unknown,
    rawHour: unknown,
    rawMinute: unknown,
) => {
    if (typeof rawTime === 'string') {
        const match = rawTime.trim().match(/^([01]?\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?$/u);
        if (match) {
            const hour = asRefreshHour(match[1], DEFAULT_OUTCOME_SUMMARY_SETTINGS.refreshHourLocal);
            const minute = asRefreshMinute(match[2], DEFAULT_OUTCOME_SUMMARY_SETTINGS.refreshMinuteLocal);
            return {
                hour,
                minute,
                localTime: `${padTimePart(hour)}:${padTimePart(minute)}`,
            };
        }
    }

    const hour = asRefreshHour(rawHour, DEFAULT_OUTCOME_SUMMARY_SETTINGS.refreshHourLocal);
    const minute = asRefreshMinute(rawMinute, DEFAULT_OUTCOME_SUMMARY_SETTINGS.refreshMinuteLocal);
    return {
        hour,
        minute,
        localTime: `${padTimePart(hour)}:${padTimePart(minute)}`,
    };
};

const canUseLocalOutcomeSummarySettingsFallback = () => (
    import.meta.env.DEV === true
);

const httpStatusFromError = (error: unknown): number | null => {
    if (!error || typeof error !== 'object') return null;
    const record = error as {
        response?: {
            status?: unknown;
        };
        status?: unknown;
    };
    const rawStatus = record.response?.status ?? record.status;
    const status = Number(rawStatus);
    return Number.isFinite(status) ? status : null;
};

const shouldUseLocalOutcomeSummarySettingsFallback = (error: unknown) => (
    canUseLocalOutcomeSummarySettingsFallback() && httpStatusFromError(error) === null
);

type UseOutcomeSummarySettingsOptions = {
    enabled?: boolean;
};

export const normalizeOutcomeSummarySettings = (
    raw?: OutcomeLedgerSettingsResponse | null,
): OutcomeSummarySettings => {
    const enabledValue = raw?.enabled ?? raw?.dailyUpdatesEnabled ?? raw?.daily_updates_enabled;
    const privacyAcceptedAt = raw?.privacyAcceptedAt ?? raw?.privacy_accepted_at ?? null;
    const refreshTime = normalizeRefreshLocalTime(
        raw?.refreshLocalTime ?? raw?.refresh_local_time,
        raw?.refreshHourLocal ?? raw?.refresh_hour_local,
        raw?.refreshMinuteLocal ?? raw?.refresh_minute_local,
    );

    return {
        enabled: asBoolean(enabledValue, DEFAULT_OUTCOME_SUMMARY_SETTINGS.enabled) && Boolean(privacyAcceptedAt),
        refreshHourLocal: refreshTime.hour,
        refreshMinuteLocal: refreshTime.minute,
        refreshLocalTime: refreshTime.localTime,
        timezone: raw?.timezone || DEFAULT_OUTCOME_SUMMARY_SETTINGS.timezone,
        lastGeneratedAt: raw?.lastGeneratedAt || raw?.last_generated_at || DEFAULT_OUTCOME_SUMMARY_SETTINGS.lastGeneratedAt,
        privacyAcceptedAt,
        privacyNoticeVersion: raw?.privacyNoticeVersion ?? raw?.privacy_notice_version ?? null,
    };
};

export const useOutcomeSummarySettings = (
    workspaceId?: string | null,
    options: UseOutcomeSummarySettingsOptions = {},
) => {
    const shouldLoadSettings = options.enabled ?? true;
    const [settings, setSettings] = useState<OutcomeSummarySettings>(DEFAULT_OUTCOME_SUMMARY_SETTINGS);
    const [status, setStatus] = useState<OutcomeSummarySettingsStatus>(
        workspaceId && shouldLoadSettings ? 'loading' : 'ready',
    );
    const [error, setError] = useState<Error | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!workspaceId || !shouldLoadSettings) {
            setSettings(DEFAULT_OUTCOME_SUMMARY_SETTINGS);
            setStatus('ready');
            setError(null);
            return;
        }

        let cancelled = false;
        setStatus('loading');
        setError(null);

        featureService.getOutcomeLedgerSettings(workspaceId)
            .then((nextSettings) => {
                if (cancelled) return;
                setSettings(normalizeOutcomeSummarySettings(nextSettings));
                setStatus('ready');
            })
            .catch((nextError) => {
                if (cancelled) return;
                if (shouldUseLocalOutcomeSummarySettingsFallback(nextError)) {
                    setSettings(normalizeOutcomeSummarySettings(readStoredOutcomeSummarySettings(workspaceId)));
                    setStatus('ready');
                    setError(null);
                    return;
                }

                setError(nextError instanceof Error ? nextError : new Error(String(nextError)));
                setStatus('error');
            });

        return () => {
            cancelled = true;
        };
    }, [shouldLoadSettings, workspaceId]);

    const setEnabled = useCallback(async (enabled: boolean) => {
        const previousSettings = settings;
        const optimisticSettings = enabled
            ? {
                ...settings,
                enabled,
                privacyAcceptedAt: settings.privacyAcceptedAt || new Date().toISOString(),
                privacyNoticeVersion: settings.privacyNoticeVersion || OUTCOME_SUMMARY_PRIVACY_NOTICE_VERSION,
            }
            : { ...settings, enabled };

        setSettings(optimisticSettings);
        setIsSaving(true);
        setError(null);

        if (!workspaceId) {
            setSettings(optimisticSettings);
            setIsSaving(false);
            return optimisticSettings;
        }

        try {
            const savedSettings = await featureService.updateOutcomeLedgerSettings(workspaceId, enabled
                ? {
                    enabled: true,
                    dailyUpdatesEnabled: true,
                    acceptPrivacyNotice: true,
                    privacyNoticeVersion: OUTCOME_SUMMARY_PRIVACY_NOTICE_VERSION,
                }
                : {
                    enabled: false,
                    dailyUpdatesEnabled: false,
                });
            const normalizedSettings = normalizeOutcomeSummarySettings(savedSettings);
            if (canUseLocalOutcomeSummarySettingsFallback()) {
                writeStoredOutcomeSummarySettings(workspaceId, normalizedSettings);
            }
            setSettings(normalizedSettings);
            setStatus('ready');
            return normalizedSettings;
        } catch (nextError) {
            setSettings(previousSettings);
            setError(nextError instanceof Error ? nextError : new Error(String(nextError)));
            setStatus('error');
            throw nextError;
        } finally {
            setIsSaving(false);
        }
    }, [settings, workspaceId]);

    return {
        settings,
        status,
        error,
        isLoading: status === 'loading',
        isSaving,
        setEnabled,
    };
};
